import React, { useEffect, useRef, useState } from 'react';
import googleLogo from '../../assets/google-g.svg';
import { loadGoogleIdentity } from '../../api/googleIdentity';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const GoogleSignIn = ({ clientId }) => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const button = useRef(null);
  const exchanging = useRef(false);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const container = button.current;
    setError('');
    if (!clientId) {
      setStatus('unavailable');
      setError('Google login is not configured. Please use password login.');
      return;
    }
    setStatus('loading');
    loadGoogleIdentity().then((identity) => {
      if (!active) return;
      identity.initialize({
        client_id: clientId,
        auto_select: false,
        callback: async (response) => {
          if (!active || exchanging.current) return;
          exchanging.current = true;
          setStatus('exchanging');
          setError('');
          try {
            const result = await loginWithGoogle(response.credential);
            if (!active) return;
            if (result.success) navigate(result.redirectTo, { replace: true });
            else setError(result.message);
          } catch {
            if (active) setError('Google login is unavailable. Please retry or use password login.');
          } finally {
            exchanging.current = false;
            if (active) setStatus('ready');
          }
        },
      });
      identity.renderButton(container, { type: 'standard', theme: 'outline', size: 'large', shape: 'pill', text: 'signin_with' });
      setStatus('ready');
    }).catch(() => {
      if (!active) return;
      setStatus('unavailable');
      setError('Google sign-in could not be loaded. Check your connection or use password login.');
    });
    return () => {
      active = false;
      container.replaceChildren();
    };
  }, [clientId, loginWithGoogle, navigate, attempt]);

  return <div className="google-login" aria-label="Google login">
    <div className="login-divider">or</div>
    <div ref={button} className="google-login-button" hidden={status !== 'ready'} />
    {status !== 'ready' && <button type="button" className="google-login-placeholder"
      aria-label={status === 'unavailable' ? 'Sign in with Google' : undefined}
      aria-hidden={status !== 'unavailable'} disabled>
      <img src={googleLogo} width="20" height="20" alt="" />
    </button>}
    {status === 'loading' && <p role="status">Loading Google sign-in...</p>}
    {status === 'exchanging' && <p role="status">Signing in with Google...</p>}
    {error && <div className="error-message" role={clientId ? 'alert' : 'status'}>{error}</div>}
    {status === 'unavailable' && clientId && <button type="button" className="guest-button" onClick={() => setAttempt((value) => value + 1)}>Retry Google sign-in</button>}
  </div>;
};

export default GoogleSignIn;
